/**
 * Reunification API service for Aegix One.
 *
 * ONE is a guardian-facing app — API calls are scoped to the
 * current user's linked students and events.
 *
 * Uses typed `client.GET/POST/...` per ADR 113 §Decision. Self-routes
 * (`guardians/me/`, `guardians/pre-verify/`, `guardians/arrived/`,
 * `guardians/check-in/`) and `students/{id}/report-offsite/` go through
 * `client.ky` instead of the typed client.
 */

import { client, unwrap } from '../lib/http-client';
import type { components } from '@tiaki/http-client/types';
import type {
  ReunificationStatus,
  StudentStatus,
  GuardianStatus,
} from '../types/reunification';

// --- Types matching backend serializers ---
//
// `status` fields carry the schema-derived lowercase enums so a
// re-introduced UPPERCASE comparison fails tsc instead of silently never
// matching a real payload.

/**
 * A student linked to the current guardian, as emitted by
 * `GuardianStudentSerializer` (`GET /api/v2/reunification/guardian/students/`).
 *
 * This is the REAL payload shape — `first_name`/`last_name` (no
 * `student_name`), roster enrollment `status` (`active`/`inactive`/
 * `transferred`/`withdrawn` — NOT the per-event reunification status), and
 * no `updated_at`. `public_id` is the RosterPerson public_id, which event
 * student rows join against via `roster_person_public_id`.
 */
export type GuardianStudent = components['schemas']['GuardianStudent'];

/**
 * Response envelope of the guardian students route: `{ count, results,
 * message? }` — a plain list, NOT DRF-paginated (no `next`/`previous`,
 * no query params).
 */
export type GuardianStudentsResponse =
  components['schemas']['GuardianStudentsResponse'];

/** Roster enrollment status values the guardian students payload emits. */
export type RosterEnrollmentStatus =
  components['schemas']['RosterPersonEnrollmentStatus'];

/**
 * Transitional type for the visitor-management flows
 * (PreCheckIn / CarRider / PickupDelegation / StudentCheckIn /
 * GeofenceLifecycleManager), which still assume the RosterPerson integer
 * PKs `id` and `school_id`. `GuardianStudentSerializer` does NOT emit
 * either field, so against real payloads they are always `undefined` and
 * each flow's existing invalid-data guard fires. Typed optional here so
 * that reality is visible to tsc instead of hidden behind `as unknown`;
 * a follow-up must add a real join key to the payload (or move the VM
 * flows onto public_id-based endpoints) before those flows can work.
 */
export type GuardianStudentWithLegacyIds = GuardianStudent & {
  id?: number;
  school_id?: number;
};

/** Display name for a guardian-linked student (payload has first/last, not `student_name`). */
export function guardianStudentName(
  s: Pick<GuardianStudent, 'first_name' | 'last_name'>,
): string {
  return [s.first_name, s.last_name].filter(Boolean).join(' ');
}

export interface ReunificationEventSummary {
  public_id: string;
  school_name: string;
  incident_type_display: string;
  plan_name: string | null;
  primary_site_name: string | null;
  status: ReunificationStatus;
  status_display: string;
  is_drill: boolean;
  started_at: string | null;
  completed_at: string | null;
  stats: {
    total_students: number;
    accounted: number;
    reunified: number;
    released: number;
    unaccounted: number;
    missing: number;
    medical: number;
  };
}

export interface StudentReunification {
  public_id: string;
  student_name: string;
  grade: string;
  homeroom: string;
  status: StudentStatus;
  status_display: string;
  is_cleared: boolean;
  updated_at: string;
  id?: number;
  school_id?: number;
  /**
   * RosterPerson.public_id of the student. Present on event
   * student rows (whose own `public_id` is the per-event
   * StudentReunification record) so they can be joined against the
   * guardian linked-students listing, which is keyed by RosterPerson id.
   */
  roster_person_public_id?: string;
}

export interface GuardianSelf {
  public_id: string;
  guardian_name: string;
  status: GuardianStatus;
  status_display: string;
  arrived_at: string | null;
  verified_at: string | null;
  student_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// --- API Calls ---

/** List active reunification events visible to the current user. */
export async function getActiveEvents(): Promise<
  PaginatedResponse<ReunificationEventSummary>
> {
  const result = await client.GET('/api/v2/reunification/events/', {
    params: { query: { status: 'active' } as never },
  });
  return unwrap(result) as unknown as PaginatedResponse<ReunificationEventSummary>;
}

/** List all reunification events (active + completed). */
export async function getAllEvents(): Promise<
  PaginatedResponse<ReunificationEventSummary>
> {
  const result = await client.GET('/api/v2/reunification/events/', {
    params: { query: { ordering: '-created_at' } as never },
  });
  return unwrap(result) as unknown as PaginatedResponse<ReunificationEventSummary>;
}

/** Get event detail. */
export async function getEventDetail(
  eventId: string,
): Promise<ReunificationEventSummary> {
  const result = await client.GET('/api/v2/reunification/events/{public_id}/', {
    params: { path: { public_id: eventId } as never },
  });
  return unwrap(result) as unknown as ReunificationEventSummary;
}

/** Get students for an event. */
export async function getEventStudents(
  eventId: string,
): Promise<StudentReunification[]> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/students/',
    {
      params: {
        path: { event_public_id: eventId } as never,
      },
    },
  );
  return unwrap(result) as unknown as StudentReunification[];
}

/**
 * Guardian self-routes (`guardians/me/`, `guardians/pre-verify/`,
 * `guardians/arrived/`, `guardians/check-in/` — no public_id parameter;
 * the backend resolves "current user" server-side). These go through
 * `client.ky` rather than the typed client.
 */
export async function getGuardianSelf(eventId: string): Promise<GuardianSelf> {
  const response = await client.ky.get(
    `api/v2/reunification/events/${eventId}/guardians/me/`,
  );
  return (await response.json()) as GuardianSelf;
}

export async function preVerifySelf(eventId: string): Promise<GuardianSelf> {
  const response = await client.ky.post(
    `api/v2/reunification/events/${eventId}/guardians/pre-verify/`,
  );
  return (await response.json()) as GuardianSelf;
}

export async function markArrived(eventId: string): Promise<GuardianSelf> {
  const response = await client.ky.post(
    `api/v2/reunification/events/${eventId}/guardians/arrived/`,
  );
  return (await response.json()) as GuardianSelf;
}

export async function checkInSelf(eventId: string): Promise<GuardianSelf> {
  const response = await client.ky.post(
    `api/v2/reunification/events/${eventId}/guardians/check-in/`,
  );
  return (await response.json()) as GuardianSelf;
}

/**
 * Get students linked to the current guardian.
 *
 * Typed straight off the generated schema — no `as unknown`. The
 * route takes no query params and is not paginated; the envelope is
 * `{ count, results, message? }` with `GuardianStudent` rows.
 */
export async function getMyStudents(): Promise<GuardianStudentsResponse> {
  const result = await client.GET(
    '/api/v2/reunification/guardian/students/',
    {},
  );
  return unwrap(result);
}

/**
 * Report a student as safe offsite (`students/{id}/report-offsite/` via
 * `client.ky`).
 */
export async function reportStudentSafeOffsite(
  eventId: string,
  studentId: string,
  data: { location?: string; notes?: string },
): Promise<StudentReunification> {
  const response = await client.ky.post(
    `api/v2/reunification/events/${eventId}/students/${studentId}/report-offsite/`,
    { json: data },
  );
  return (await response.json()) as StudentReunification;
}

/** Get notifications for the current user. */
export async function getNotifications(filters?: {
  is_read?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<
  PaginatedResponse<{
    id: number;
    title: string;
    body: string;
    category: string;
    priority: string;
    is_read: boolean;
    created_at: string;
    action_url: string | null;
  }>
> {
  const result = await client.GET('/api/v2/notifications/', {
    params: {
      query: {
        page_size: filters?.page_size ?? 50,
        is_read: filters?.is_read,
        search: filters?.search,
        page: filters?.page,
      } as never,
    },
  });
  return unwrap(result) as unknown as PaginatedResponse<{
    id: number;
    title: string;
    body: string;
    category: string;
    priority: string;
    is_read: boolean;
    created_at: string;
    action_url: string | null;
  }>;
}

/** Mark notification as read. */
export async function markNotificationRead(id: number): Promise<void> {
  const result = await client.POST('/api/v2/notifications/{id}/read', {
    params: { path: { id } as never },
    body: {} as never,
  });
  unwrap(result);
}

/** Get unread notification count. */
export async function getUnreadCount(): Promise<{ unread_count: number }> {
  const result = await client.GET('/api/v2/notifications/unread-count', {});
  return unwrap(result) as { unread_count: number };
}
